import { useTranslation } from 'next-i18next';
import { useAtom, useAtomValue } from 'jotai';
import Image from 'next/image';

import { Heading, Text } from '@restworld/ui-ds-base';
import { BulbIcon, cn, customImageLoader, getAssetSrc, useResponsiveBreakPoints } from '@restworld/utils-common';

import SpontaneousApplication from '../../spontaneous-application';
import { ViewOtherRestaurantsButton } from './restaurant-job-positions';
import { getRestaurantPopupInfoAtom, hasUserAppliedSpontaneousApplicationAtom } from '../../worker-entry-point';

export type RestaurantSpontaneousApplicationBannerProps = {
  variant: 'active-job-positions' | 'top-banner';
  slug: string;
  customTitle?: string;
};

export const RestaurantSpontaneousApplicationBanner = ({
  variant,
  customTitle,
}: RestaurantSpontaneousApplicationBannerProps) => {
  const { t } = useTranslation('restaurant-showcase-page');
  const { isDesktop } = useResponsiveBreakPoints();
  const [restaurantPopupInfo] = useAtom(getRestaurantPopupInfoAtom);
  const didUserSpontaneousApply = useAtomValue(hasUserAppliedSpontaneousApplicationAtom);
  const ctasEl = (
    <CtasContainer {...{ variant }}>
      <SpontaneousApplication
        restaurantID={restaurantPopupInfo?.restaurantId}
        label={didUserSpontaneousApply ? t('show_application_status') : t('spontaneous_application')}
        fullwidth={!isDesktop}
      />
      {variant === 'active-job-positions' && (
        <ViewOtherRestaurantsButton variant="tertiary" fullwidth={false} className={'px-0 lg:w-fit w-full'} />
      )}
    </CtasContainer>
  );
  return (
    <div className="flex flex-col w-full gap-6 items-start">
      <StyledBanner {...{ variant }}>
        <div className="flex flex-row lg:items-start items-center gap-6 w-full">
          <div className="flex flex-col flex-1 items-start">
            <span className="flex flex-row items-center gap-1.5 mb-2">
              {variant === 'top-banner' && <BulbIcon />}
              <Heading size={3}>{customTitle ?? t('no_offers')}</Heading>
            </span>
            <Text size={2} className={variant === 'active-job-positions' ? 'mb-6' : 'mb-4'} color="muted">
              {didUserSpontaneousApply ? t('already_sent_and_explore_desc') : t('send_spontaneous_application_desc')}
            </Text>
            {variant === 'active-job-positions' && (
              <div className="lg:flex hidden flex-col  w-full items-start ">{ctasEl}</div>
            )}
            {variant === 'top-banner' && ctasEl}
          </div>
          {variant === 'active-job-positions' && (
            <Image
              src={getAssetSrc('/utils-assets/catapult.svg')}
              width={150}
              height={141}
              alt={t('restaurant_offers')}
              loader={customImageLoader}
              className="lg:w-[150px] w-[114px] lg:h-[141px] h-[105px]"
            />
          )}
        </div>
        {variant === 'active-job-positions' && (
          <div className="lg:hidden flex flex-col  w-full items-start ">{ctasEl}</div>
        )}
      </StyledBanner>
    </div>
  );
};

export const StyledBanner = ({
  variant,
  className,
  ...props
}: Pick<RestaurantSpontaneousApplicationBannerProps, 'variant'> & React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'flex',
      variant === 'active-job-positions' && 'p-8 flex-col lg:items-start gap-6 border border-gray-300 rounded-2xl',
      className
    )}
    {...props}
  />
);

const CtasContainer = ({
  variant,
  className,
  ...props
}: Pick<RestaurantSpontaneousApplicationBannerProps, 'variant'> & React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('grid xl:grid-cols-2 lg:gap-6 gap-4 w-full', variant === 'top-banner' && 'lg:grid-cols-1', className)}
    {...props}
  />
);
